import {
  isCurrentStepValid,
  removeDollar,
} from '@next-app/utils/CheckoutHelper';
import { CheckoutState } from './Checkout.interface';
import {
  isNotEmptyObject,
  isNotNullOrUndefinedorEmpty,
  isEmptyArray,
} from '@next-app/utils/common-utils';
import { PAYMENT } from '@next-app/constants/CheckoutConstants';

export const selectCheckoutState = (state: CheckoutState) => state.checkout;

export const selectCheckoutDetails = (state: CheckoutState) => {
  return selectCheckoutState(state).checkoutLoadDetails;
};

export const selectIsCheckoutDetailsLoaded = (state: CheckoutState) => {
  return isNotEmptyObject(selectCheckoutDetails(state));
};

export const selectGooglePaymentRequest = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.googlePayPaymentRequest;
};

export const selectHasItemFromShippingList = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.clearTheListOrder;
};

export const selectHasItemFromRegistry = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.registryOrder;
};

export const selectProfileAddressinfo = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.profileAddressinfo;
};

export const selectAllProfileAddresses = (state: CheckoutState) => {
  return selectProfileAddressinfo(state)?.addresses;
};

const getProcessedAddresses = (state: CheckoutState) => {
  const sgInfo = selectSgInfo(state) || {};
  let addressId = sgInfo?.addressId || '';
  let allAddresses = selectAllProfileAddresses(state) || [];

  if (
    isNotEmptyObject(sgInfo.address) &&
    (allAddresses.length === 0 || sgInfo?.addressId === '')
  ) {
    allAddresses = [sgInfo.address, ...allAddresses];
    addressId = 'unsaved';
  }

  return { allAddresses, addressId };
};

export const selectDefaultProfileAddress = (state: CheckoutState) => {
  const sgInfo = selectSgInfo(state) || {};
  const { allAddresses, addressId } = getProcessedAddresses(state);

  return (
    allAddresses.find(
      ({ repositoryId }: { repositoryId: string }) =>
        repositoryId === sgInfo.addressId,
    ) || (addressId === 'unsaved' ? sgInfo.address : undefined)
  );
};

export const selectAllProfileAddress = (state: CheckoutState) => {
  const { allAddresses, addressId } = getProcessedAddresses(state);
  return { allAddresses, addressId };
};

export const selectHasDefaultAddress = (state: CheckoutState) => {
  return isNotEmptyObject(selectDefaultProfileAddress(state));
};

export const selectSgInfo = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.sgInfo;
};

export const selectEmailInfo = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.emailInfo;
};

export const selectOrderShipType = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.orderShipType;
};

export const selectIsPayPalEnabled = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.payPalEnabled;
};

export const selectPaymentDetailInfo = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.paymentDetailInfo;
};

export const selectLakeshoreBucks = (state: CheckoutState) => {
  return selectPaymentDetailInfo(state)?.lakeshoreBucks || [];
};

export const selectPaymentMethods = (state: CheckoutState) => {
  return selectPaymentDetailInfo(state)?.paymentMethods;
};

export const selectNoSavedCreditCard = (state: CheckoutState) => {
  const paymentMethodsObj = selectPaymentMethods(state);
  if (paymentMethodsObj?.paymentMethods?.length > 0) {
    const savedCreditCrads = paymentMethodsObj.paymentMethods.some(
      (payment) => payment?.paymentMethod === 'creditCard',
    );
    return !savedCreditCrads;
  } else {
    return true;
  }
};

export const selectNewSmInfo = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.newSmInfo;
};

export const selectCheckoutSteps = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.currentStepInfo?.checkoutSteps;
};

export const selectPaymentGroups = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.paymentDetailInfo?.paymentsInfo
    ?.paymentGroups;
};

export const selectDigitalOnlyWebItemsInOrder = (state: CheckoutState) => {
  return selectCheckoutState(state).checkoutLoadDetails
    ?.digitalOnlyWebItemsInOrder;
};

export const selectIsShippingAddressStep = (state: CheckoutState) => {
  const checkoutSteps = selectCheckoutSteps(state) || [];
  return isCurrentStepValid(checkoutSteps, 'shippingAddress');
};

export const selectShowShippingMethodStep = (state: CheckoutState) => {
  const isShipOrder = selectIsShipOrder(state);
  const value = isShipOrder
    ? selectIsShippingAddressCollapsed(state)
    : selectEmailSectionCollapsed(state);
  return value;
};

export const selectIsPaymentMethodStep = (state: CheckoutState) => {
  const checkoutSteps = selectCheckoutSteps(state) || [];
  return isCurrentStepValid(checkoutSteps, 'paymentMethod');
};

export const selectShippingMethod = (state: CheckoutState) => {
  return selectNewSmInfo(state)?.shippingMethod;
};

export const selectShippingMethods = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.shippinginfo?.shippingMethods;
};

export const IsShippingMethodCollapsed = (state: CheckoutState) => {
  const checkoutSteps = selectCheckoutSteps(state) || [];
  return (
    checkoutSteps?.find((el) => el?.key === 'shippingMethod')?.value || false
  );
};

export const selectIsPaymentSectionCollapsed = (state: CheckoutState) => {
  const checkoutSteps = selectCheckoutSteps(state) || [];
  return (
    checkoutSteps?.find((el) => el?.key === 'paymentMethod')?.value || false
  );
};

export const selectIsShippingAddressCollapsed = (state: CheckoutState) => {
  const checkoutSteps = selectCheckoutSteps(state) || [];
  return (
    checkoutSteps?.find((el) => el?.key === 'shippingAddress')?.value || false
  );
};

export const selectIsPureBopisOrder = (state: CheckoutState) => {
  return selectOrderShipType(state) === 'instorePickUpOnly';
};

export const selectIsPureDigitalOrder = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.digitalOnlyOrder === true;
};

export const selectIsPureShipOrder = (state: CheckoutState) => {
  const orderShipType = selectOrderShipType(state);
  const isPureDigitalOrder = selectIsPureDigitalOrder(state);
  return orderShipType === 'webOnly' && !isPureDigitalOrder;
};

export const selectIsOnlyShipOrder = (state: CheckoutState) => {
  const orderShipType = selectOrderShipType(state);
  return orderShipType === 'webOnly';
};

export const selectIsBopisOrder = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.orderSummaryInfo?.isBopisOrder;
};

export const selectIsShipOrder = (state: CheckoutState) => {
  const isShipOrder = selectWebSubTotalInfo(state);
  return isShipOrder && isNotEmptyObject(isShipOrder);
};

export const selectIsDigitalOrder = (state: CheckoutState) => {
  const isDigitalOrder = selectEmailDeliverySubtotalInfo(state);
  return isDigitalOrder && isNotEmptyObject(isDigitalOrder);
};

export const selectIsMixOrder = (state: CheckoutState) => {
  const orderShipType = selectOrderShipType(state);
  return orderShipType === 'webAndInstorePickup';
};

export const selectPickUpPersonInfo = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.shippinginfo?.pickupPersonInfo;
};

export const selectStoreDetails = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.shippinginfo?.storeDetails;
};

export const SelectSgInfoAddress = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.sgInfo;
};
export const selectShippingAddressInfo = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.shippingAddressInfo;
};

export const selectHintAddress = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.shippingAddressInfo?.hintAddress;
};

export const selectShippingAddress = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.profileInfo?.profile?.shippingAddress;
};

export const selectSelectedShippingMethod = (state: CheckoutState) => {
  const shippingMethod = selectShippingMethod(state);
  const shippingMethods = selectShippingMethods(state);

  const { keyInfo: { repositoryId = '' } = {} } = shippingMethod;
  return shippingMethods?.find(
    (el) => el?.keyInfo?.repositoryId === repositoryId,
  );
};

export const selectIsInternationalOrder = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.shippinginfo?.isInternationalOrder;
};

export const selectOrderInfo = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.shoppingCartDetailInfo?.orderinfo;
};

export const selectWebSubTotalInfo = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.orderSummaryInfo?.webSubTotalInfo;
};

export const selectEmailDeliverySubtotalInfo = (state: CheckoutState) => {
  return selectOrderSummaryInfo(state)?.emailDeliverySubtotalInfo;
};

export const selectInstorePickupSubTotalInfo = (state: CheckoutState) => {
  return selectOrderSummaryInfo(state)?.instorePickupSubTotalInfo;
};

export const selectOrderSummaryInfo = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.orderSummaryInfo;
};

export const selectCommerceItems = (state: CheckoutState) => {
  return selectOrderInfo(state)?.commerceItems;
};

export const selectEmailInfoPhoneNumber = (state: CheckoutState) => {
  return selectEmailInfo(state)?.phoneNumber;
};

export const selectBillingFromLoadCheckoutAddress = (state: CheckoutState) => {
  return selectPaymentDetailInfo(state)?.billingAddress;
};

export const selectBillingAddress = (state: CheckoutState) => {
  return selectCheckoutState(state).billingRequest;
};
export const selectEmailInfoEmailOptIn = (state: CheckoutState) => {
  return selectEmailInfo(state)?.emailOptIn;
};
export const selectIsEmailDeliveryItemInCart = (state: CheckoutState) => {
  return isNotEmptyObject(selectEmailDeliverySubtotalInfo(state));
};

export const selectIsBopisItemInCart = (state: CheckoutState) => {
  return isNotEmptyObject(selectInstorePickupSubTotalInfo(state));
};

export const selectCartSummary = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.cartSummary;
};

export const selectLoyaltyInfo = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.loyaltyInfo;
};

export const selectRewardsInfo = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.rewardsInfo;
};

export const selectPaymentsInfo = (state: CheckoutState) => {
  return selectPaymentDetailInfo(state)?.paymentsInfo;
};

export const selectMainPaymentGroup = (state: CheckoutState) => {
  return selectPaymentsInfo(state)?.mainPaymentGroup;
};

export const selectEmailSectionCollapsed = (state: CheckoutState) => {
  const checkoutSteps = selectCheckoutSteps(state) || [];
  return (
    checkoutSteps?.find((el) => el?.key === 'emailRegister')?.value || false
  );
};

export const selectIsPureFurniture = (state: CheckoutState) => {
  const cartItem = selectCommerceItems(state) || [];
  return cartItem?.every((item) => item?.furniture) || false;
};

export const selectCountries = (state: CheckoutState) => {
  return selectProfileAddressinfo(state)?.countries;
};

export const selectIsSubmitOrder = (state: CheckoutState) => {
  return selectCheckoutState(state).isSubmitOrder;
};

export const selectPaymentMethodType = (state: CheckoutState) => {
  return selectCheckoutState(state).paymentMethodType;
};

// loadCheckout Additional Msgs selector
export const selectAdditionalMessage = (state: CheckoutState) => {
  return selectCheckoutState(state).checkoutAdditionalMsg;
};

export const selectIsNewAddress = (state: CheckoutState) => {
  return selectAdditionalMessage(state)?.oldAddress?.isNewAddress;
};

export const selectIsReadyForSubmit = (state: CheckoutState) => {
  return selectOrderSummaryInfo(state)?.readyForCheckout;
};

export const selectGiftingItemFrom = (state: CheckoutState) => {
  return selectCheckoutState(state)?.giftItemFrom;
};

export const selectIsGiftItemAnonymous = (state: CheckoutState) => {
  return selectCheckoutState(state)?.isGiftItemAnonymous;
};

export const selectIsPureShipTypeOrder = (state: CheckoutState) => {
  const orderShipType = selectOrderShipType(state);
  const isDigitalOrder = isNotEmptyObject(
    selectEmailDeliverySubtotalInfo(state),
  );

  return orderShipType === 'webOnly' && !isDigitalOrder;
};

export const selectDisablePaymentList = (state: CheckoutState) => {
  const { displayTotal = '' } = selectOrderSummaryInfo(state) || {};
  const lakeshoreBucks = selectLakeshoreBucks(state) || [];
  const { isGiftCertificateOverTotal } = selectPaymentsInfo(state) || {};
  const { additionalPaymentRequired } = selectPaymentDetailInfo(state) || {};

  const disableForLakeshoreBuck =
    !additionalPaymentRequired &&
    lakeshoreBucks.length > 0 &&
    removeDollar(displayTotal) === 0;
  const isDisableAllList =
    isGiftCertificateOverTotal || disableForLakeshoreBuck;

  return isDisableAllList;
};

export const selectBillingAddressFormErrors = (state: CheckoutState) => {
  return selectBillingAddress(state).billingAddressForErrors;
};

export const selectIsPayPalPaymentMethodAuthorized = (state: CheckoutState) => {
  const mainPaymentGroup = selectMainPaymentGroup(state);
  return (
    mainPaymentGroup?.paymentMethod === 'payPal' && mainPaymentGroup?.amount > 0
  );
};

export const selectIsGooglePayAuthorized = (state: CheckoutState) => {
  const googlePayPaymentRequest =
    selectCheckoutDetails(state)?.googlePayPaymentRequest;
  const mainPaymentGroup = selectMainPaymentGroup(state);
  return (
    mainPaymentGroup?.paymentProvider === 'GooglePay' &&
    isNotNullOrUndefinedorEmpty(googlePayPaymentRequest)
  );
};

export const selectReloadLoadCheckoutDetails = (state: CheckoutState) => {
  return selectCheckoutState(state).reloadLoadCheckoutDetails;
};

export const selectSameAsBillingAddress = (state: CheckoutState) => {
  return selectCheckoutState(state).sameAsDeliveryAddress;
};

export const selectIsPoaSetAsDefault = (state: CheckoutState) => {
  return selectPaymentMethods(state).defaultPaymentType == PAYMENT.ON_ACCOUNT;
};

export const selectCreditCardFormErrors = (state: CheckoutState) => {
  return selectBillingAddress(state).creditCardFormErrors;
};

export const selectIsGiftCardItemInOrder = (state: CheckoutState) => {
  return selectPaymentsInfo(state)?.giftCardItemInOrder;
};

export const selectOrderComments = (state: CheckoutState) => {
  return selectCheckoutState(state).orderComments;
};

export const selectIsGiftCertificateOverTotal = (state: CheckoutState) => {
  const { isGiftCertificateOverTotal = false } =
    selectPaymentsInfo(state) || {};
  return isGiftCertificateOverTotal;
};

export const selectTaxExemptOpted = (state: CheckoutState) => {
  return selectPaymentDetailInfo(state)?.taxExemptOpted;
};

export const selectClosenessQualifiers = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.closenessQualifiers;
};

export const selectShowRegistryAddressModal = (state: CheckoutState) => {
  return selectCheckoutDetails(state)?.showRegistryAddressModal;
};
